import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { Button } from '../../../components/ui'
import { getApiErrorMessage } from '../../../utils/apiError'
import {
  getCurrentLegalDocumentActionStatuses,
  recordLegalDocumentActions,
} from '../api/legalDocumentApi'
import {
  AI_REQUIRED_DOCUMENT_TYPES,
  AI_CONSENT_DOCUMENT_TYPES,
  areLegalDocumentTypesSatisfied,
  buildLegalDocumentActions,
  createLegalDocumentSelectionsFromStatuses,
  isLegalActionRequestConflict,
  LEGAL_ACTION_CONTEXT,
  LEGAL_DOCUMENT_TYPE,
  shouldRefreshLegalDocuments,
} from '../lib/legalDocuments'
import LegalDocumentChecklist from './LegalDocumentChecklist'

function canActivateAi(headers, selections) {
  const requiredTypes = [
    LEGAL_DOCUMENT_TYPE.AI_LIMITATIONS,
    ...AI_CONSENT_DOCUMENT_TYPES,
  ]
  return requiredTypes.every(documentType => {
    const header = headers.find(item => item.documentType === documentType)
    return header && selections[header.legalDocumentId]
  })
}

function canActivateAiFromStatuses(statuses) {
  return areLegalDocumentTypesSatisfied(statuses, AI_REQUIRED_DOCUMENT_TYPES)
}

function getAiConsentTag(header) {
  return header.documentType === LEGAL_DOCUMENT_TYPE.AI_LIMITATIONS
    ? '확인 필요'
    : '선택 동의'
}

function getAiConsentDescription(header) {
  return AI_CONSENT_DOCUMENT_TYPES.includes(header.documentType)
    ? '미동의 시 AI 코칭 이용 불가'
    : ''
}

export default function AiLegalConsentDialog({ onClose, onGranted }) {
  const panelRef = useRef(null)
  const pendingRequestRef = useRef(null)
  const [headers, setHeaders] = useState([])
  const [actionsByDocumentId, setActionsByDocumentId] = useState({})
  const [selections, setSelections] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  const loadDocuments = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const statusResult = await getCurrentLegalDocumentActionStatuses(
        LEGAL_ACTION_CONTEXT.AI_FIRST_USE,
      )
      const statuses = statusResult.data?.legalDocumentActionStatuses || []
      const nextActionsByDocumentId = statuses.reduce((map, status) => ({
        ...map,
        [status.legalDocumentId]: status.currentAction,
      }), {})
      const nextSelections = createLegalDocumentSelectionsFromStatuses(statuses)
      setHeaders(statuses)
      setActionsByDocumentId(nextActionsByDocumentId)
      setSelections(nextSelections)
      pendingRequestRef.current = null
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'AI 코칭 이용 안내를 불러오지 못했어요.'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments, reloadKey])

  useEffect(() => {
    const previousFocus = document.activeElement
    panelRef.current?.focus()
    return () => previousFocus?.focus?.()
  }, [])

  const submitResponses = async () => {
    if (isSubmitting || !canActivateAi(headers, selections)) return
    const selectionKey = JSON.stringify(selections)
    if (!pendingRequestRef.current || pendingRequestRef.current.selectionKey !== selectionKey) {
      pendingRequestRef.current = {
        selectionKey,
        payload: {
          actions: buildLegalDocumentActions({
            headers,
            previousActionsByDocumentId: actionsByDocumentId,
            selections,
          }),
        },
      }
    }

    setIsSubmitting(true)
    setError('')
    try {
      await recordLegalDocumentActions(
        LEGAL_ACTION_CONTEXT.AI_FIRST_USE,
        pendingRequestRef.current.payload,
      )
      const statusResult = await getCurrentLegalDocumentActionStatuses(
        LEGAL_ACTION_CONTEXT.AI_FIRST_USE,
      )
      const statuses = statusResult.data?.legalDocumentActionStatuses || []
      pendingRequestRef.current = null
      if (canActivateAiFromStatuses(statuses)) {
        onGranted()
      } else {
        setError('AI 코칭 이용에 필요한 응답을 확인하지 못했어요. 내용을 다시 확인해 주세요.')
      }
    } catch (requestError) {
      if (shouldRefreshLegalDocuments(requestError)) {
        setError('AI 관련 문서가 변경되었어요. 최신 내용을 다시 확인해 주세요.')
        setReloadKey(key => key + 1)
      } else if (isLegalActionRequestConflict(requestError)) {
        pendingRequestRef.current = null
        setError(getApiErrorMessage(requestError))
      } else {
        setError(getApiErrorMessage(requestError, 'AI 코칭 이용 응답을 저장하지 못했어요.'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const willActivateAi = canActivateAi(headers, selections)
  const canSubmit = headers.length > 0 && willActivateAi

  return (
    <Backdrop
      onKeyDown={event => event.key === 'Escape' && onClose()}
      onMouseDown={event => event.target === event.currentTarget && onClose()}
    >
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-consent-title"
        aria-describedby="ai-consent-description"
        tabIndex={-1}
      >
        <Header>
          <div>
            <small>AI 코칭 최초 이용 안내</small>
            <h2 id="ai-consent-title">AI 코칭을 시작하기 전에 확인해 주세요</h2>
          </div>
          <CloseButton type="button" aria-label="AI 코칭 이용 안내 닫기" onClick={onClose}>×</CloseButton>
        </Header>
        <Body>
          <Intro id="ai-consent-description">
            AI 코칭을 이용하려면 이용 유의사항을 확인하고, AI 개인정보 처리 및 국외 이전에 동의해야 합니다. 동의하지 않아도 akive의 다른 기능은 계속 이용할 수 있습니다.
          </Intro>
          <LegalDocumentChecklist
            error={error}
            getItemDescription={getAiConsentDescription}
            getItemTag={getAiConsentTag}
            headers={headers}
            isLoading={isLoading}
            optionalNotice=""
            selections={selections}
            selectAllLabel="AI 코칭 이용에 필요한 항목 모두 확인·동의"
            showSelectAll
            onChange={nextSelections => {
              setSelections(nextSelections)
              setError('')
            }}
            onRetry={() => setReloadKey(key => key + 1)}
          />
        </Body>
        <Actions>
          <Button $variant="quiet" disabled={isSubmitting} onClick={onClose}>나중에</Button>
          <Button disabled={!canSubmit || isLoading || isSubmitting} onClick={submitResponses}>
            {isSubmitting
              ? '저장 중…'
              : canSubmit
                ? '동의하고 AI 코칭 시작'
                : '모두 확인하고 동의해 주세요'}
          </Button>
        </Actions>
      </Panel>
    </Backdrop>
  )
}

const Backdrop = styled.div`
  position: fixed;
  z-index: 115;
  inset: 0;
  display: grid;
  padding: 24px;
  background: rgba(17, 27, 64, 0.42);
  place-items: center;
  @media (max-width: 640px) { padding: 0; }
`

const Panel = styled.section`
  display: grid;
  width: min(680px, 100%);
  max-height: min(820px, calc(100dvh - 48px));
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.medium};
  @media (max-width: 640px) { width: 100%; height: 100dvh; max-height: 100dvh; border: 0; border-radius: 0; }
`

const Header = styled.header`
  display: flex;
  padding: 18px 20px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  small { color: ${({ theme }) => theme.colors.primary}; font-size: 0.7rem; font-weight: 750; }
  h2 { margin: 5px 0 0; font-size: 1.08rem; }
`

const CloseButton = styled.button`
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  padding: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  background: transparent;
  font-size: 1.35rem;
`

const Body = styled.div`
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
`

const Intro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.82rem;
  line-height: 1.65;
`

const Actions = styled.footer`
  display: flex;
  padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`
